/**
 * Looping spinner arc — a single dashed circular stroke that rotates continuously.
 * Used during the "loading" phase of the refresh button.
 *
 * Composition: 60fps, 60 frames (1 s loop), 20×20 px canvas
 * Layer: shape layer with ellipse stroke (trim path 10%–60%) + rotation animator
 */
export const REFRESH_SPINNER_ANIMATION = {
  v: '5.9.0',
  fr: 60,
  ip: 0,
  op: 60,
  w: 20,
  h: 20,
  nm: 'refresh-spinner',
  ddd: 0,
  assets: [],
  layers: [
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: 'arc',
      sr: 1,
      ks: {
        o: { a: 0, k: 100 },
        r: {
          a: 1,
          k: [
            { t: 0, s: [0], e: [360], i: { x: [0.5], y: [1] }, o: { x: [0.5], y: [0] } },
            { t: 60, s: [360] },
          ],
        },
        p: { a: 0, k: [10, 10, 0] },
        a: { a: 0, k: [0, 0, 0] },
        s: { a: 0, k: [100, 100, 100] },
      },
      ao: 0,
      shapes: [
        {
          ty: 'el',
          nm: 'circle',
          p: { a: 0, k: [0, 0] },
          s: { a: 0, k: [16, 16] },
        },
        {
          ty: 'st',
          nm: 'stroke',
          c: { a: 0, k: [0.4, 0.4, 0.4, 1] },
          o: { a: 0, k: 100 },
          w: { a: 0, k: 1.8 },
          lc: 2,
          lj: 2,
          ml: 4,
        },
        {
          ty: 'tm',
          nm: 'trim',
          s: { a: 0, k: 10 },
          e: { a: 0, k: 70 },
          o: { a: 0, k: 0 },
          m: 1,
        },
        { ty: 'fl', nm: 'fill', o: { a: 0, k: 0 }, c: { a: 0, k: [0, 0, 0, 0] } },
      ],
      ip: 0,
      op: 60,
      st: 0,
      bm: 0,
    },
  ],
};

/**
 * One-shot checkmark draw animation.
 * A green circle border draws in (0→100 % trim) then a tick strokes on top.
 *
 * Composition: 60fps, 50 frames, 20×20 px canvas
 */
export const REFRESH_SUCCESS_ANIMATION = {
  v: '5.9.0',
  fr: 60,
  ip: 0,
  op: 50,
  w: 20,
  h: 20,
  nm: 'refresh-success',
  ddd: 0,
  assets: [],
  layers: [
    // Circle ring draws in frames 0-25
    {
      ddd: 0,
      ind: 1,
      ty: 4,
      nm: 'circle',
      sr: 1,
      ks: {
        o: { a: 0, k: 100 },
        r: { a: 0, k: -90 },
        p: { a: 0, k: [10, 10, 0] },
        a: { a: 0, k: [0, 0, 0] },
        s: { a: 0, k: [100, 100, 100] },
      },
      ao: 0,
      shapes: [
        {
          ty: 'el',
          nm: 'circle',
          p: { a: 0, k: [0, 0] },
          s: { a: 0, k: [16, 16] },
        },
        {
          ty: 'st',
          nm: 'stroke',
          c: { a: 0, k: [0.133, 0.635, 0.286, 1] },
          o: { a: 0, k: 100 },
          w: { a: 0, k: 1.8 },
          lc: 2,
          lj: 2,
          ml: 4,
        },
        {
          ty: 'tm',
          nm: 'trim',
          s: { a: 0, k: 0 },
          e: {
            a: 1,
            k: [
              { t: 0, s: [0], e: [100], i: { x: [0.4], y: [1] }, o: { x: [0.6], y: [0] } },
              { t: 25, s: [100] },
            ],
          },
          o: { a: 0, k: 0 },
          m: 1,
        },
        { ty: 'fl', nm: 'fill', o: { a: 0, k: 0 }, c: { a: 0, k: [0, 0, 0, 0] } },
      ],
      ip: 0,
      op: 50,
      st: 0,
      bm: 0,
    },
    // Checkmark tick draws in frames 18-40
    {
      ddd: 0,
      ind: 2,
      ty: 4,
      nm: 'tick',
      sr: 1,
      ks: {
        o: { a: 0, k: 100 },
        r: { a: 0, k: 0 },
        p: { a: 0, k: [10, 10, 0] },
        a: { a: 0, k: [0, 0, 0] },
        s: { a: 0, k: [100, 100, 100] },
      },
      ao: 0,
      shapes: [
        {
          ty: 'sh',
          nm: 'tick path',
          ks: {
            a: 0,
            k: {
              i: [
                [0, 0],
                [0, 0],
                [0, 0],
              ],
              o: [
                [0, 0],
                [0, 0],
                [0, 0],
              ],
              v: [
                [-4, 0],
                [-1.5, 2.8],
                [4, -3],
              ],
              c: false,
            },
          },
        },
        {
          ty: 'st',
          nm: 'stroke',
          c: { a: 0, k: [0.133, 0.635, 0.286, 1] },
          o: { a: 0, k: 100 },
          w: { a: 0, k: 1.8 },
          lc: 2,
          lj: 2,
          ml: 4,
        },
        {
          ty: 'tm',
          nm: 'trim',
          s: { a: 0, k: 0 },
          e: {
            a: 1,
            k: [
              { t: 18, s: [0], e: [100], i: { x: [0.4], y: [1] }, o: { x: [0.6], y: [0] } },
              { t: 40, s: [100] },
            ],
          },
          o: { a: 0, k: 0 },
          m: 1,
        },
        { ty: 'fl', nm: 'fill', o: { a: 0, k: 0 }, c: { a: 0, k: [0, 0, 0, 0] } },
      ],
      ip: 0,
      op: 50,
      st: 0,
      bm: 0,
    },
  ],
};
